'use strict';

class CanonicalJsonV1Error extends TypeError {
   constructor(message) {
      super(message);
      this.name = 'CanonicalJsonV1Error';
   }
}

const OFFLINE_PROTOCOL_CANONICALIZATION_VERSION_V1 = 'CANONICAL_JSON_V1';
const OFFLINE_PROTOCOL_ENVELOPE_VERSION_V1 = 'OFFLINE_PROTOCOL_ENVELOPE_V1';

const OFFLINE_PROTOCOL_DOMAIN_LABELS_V1 = Object.freeze({
   actionDigest: 'opsperfect.offline.action-digest.v1',
   provisionalEventDigest: 'opsperfect.offline.provisional-event-digest.v1',
   serverAcceptanceFingerprint: 'opsperfect.offline.server-acceptance-fingerprint.v1',
   localDeclarationFingerprint: 'opsperfect.offline.local-declaration-fingerprint.v1'
});

function canonicalizeJsonV1(value) {
   return canonicalizeValue(value, '$', new Set());
}

function buildCanonicalEnvelopeV1(domain, value) {
   if (!Object.values(OFFLINE_PROTOCOL_DOMAIN_LABELS_V1).includes(domain)) {
      throw new CanonicalJsonV1Error(`Unsupported offline protocol envelope domain at $: ${String(domain)}`);
   }

   return {
      canonicalizationVersion: OFFLINE_PROTOCOL_CANONICALIZATION_VERSION_V1,
      domain,
      envelopeVersion: OFFLINE_PROTOCOL_ENVELOPE_VERSION_V1,
      value
   };
}

function canonicalizeEnvelopeV1(domain, value) {
   return canonicalizeJsonV1(buildCanonicalEnvelopeV1(domain, value));
}

function canonicalizeActionDigestEnvelopeV1(value) {
   return canonicalizeEnvelopeV1(OFFLINE_PROTOCOL_DOMAIN_LABELS_V1.actionDigest, value);
}

function canonicalizeProvisionalEventDigestEnvelopeV1(value) {
   return canonicalizeEnvelopeV1(OFFLINE_PROTOCOL_DOMAIN_LABELS_V1.provisionalEventDigest, value);
}

function canonicalizeServerAcceptanceFingerprintEnvelopeV1(value) {
   return canonicalizeEnvelopeV1(OFFLINE_PROTOCOL_DOMAIN_LABELS_V1.serverAcceptanceFingerprint, value);
}

function canonicalizeLocalDeclarationFingerprintEnvelopeV1(value) {
   return canonicalizeEnvelopeV1(OFFLINE_PROTOCOL_DOMAIN_LABELS_V1.localDeclarationFingerprint, value);
}

function canonicalizeValue(value, path, ancestors) {
   if (value === null) {
      return 'null';
   }

   const valueType = typeof value;

   if (valueType === 'string') {
      return JSON.stringify(value);
   }

   if (valueType === 'boolean') {
      return value ? 'true' : 'false';
   }

   if (valueType === 'number') {
      validateNumber(value, path);
      return String(value);
   }

   if (valueType === 'object') {
      return Array.isArray(value) ? canonicalizeArray(value, path, ancestors) : canonicalizeObject(value, path, ancestors);
   }

   throw new CanonicalJsonV1Error(`Unsupported canonical JSON v1 value at ${path}: ${valueType}`);
}

function validateNumber(value, path) {
   if (!Number.isFinite(value) || !Number.isSafeInteger(value) || Object.is(value, -0)) {
      throw new CanonicalJsonV1Error(`Unsupported canonical JSON v1 number at ${path}`);
   }
}

function canonicalizeArray(value, path, ancestors) {
   rejectCycle(value, path, ancestors);

   for (let index = 0; index < value.length; index++) {
      if (!Object.prototype.hasOwnProperty.call(value, index)) {
         ancestors.delete(value);
         throw new CanonicalJsonV1Error(`Sparse arrays are not supported in canonical JSON v1 at ${path}`);
      }
   }

   const canonicalItems = value.map((item, index) => canonicalizeValue(item, `${path}[${index}]`, ancestors));
   ancestors.delete(value);
   return `[${canonicalItems.join(',')}]`;
}

function canonicalizeObject(value, path, ancestors) {
   validatePlainObject(value, path);
   rejectCycle(value, path, ancestors);

   const descriptors = Object.getOwnPropertyDescriptors(value);
   const keys = Object.keys(descriptors);

   if (Object.getOwnPropertySymbols(value).length > 0) {
      ancestors.delete(value);
      throw new CanonicalJsonV1Error(`Symbol keys are not supported in canonical JSON v1 at ${path}`);
   }

   if (Reflect.ownKeys(descriptors).length !== keys.length) {
      ancestors.delete(value);
      throw new CanonicalJsonV1Error(`Non-enumerable properties are not supported in canonical JSON v1 at ${path}`);
   }

   const canonicalProperties = keys.sort().map((key) => {
      const descriptor = descriptors[key];

      if (!descriptor.enumerable) {
         ancestors.delete(value);
         throw new CanonicalJsonV1Error(`Non-enumerable properties are not supported in canonical JSON v1 at ${path}.${key}`);
      }

      if (!Object.prototype.hasOwnProperty.call(descriptor, 'value')) {
         ancestors.delete(value);
         throw new CanonicalJsonV1Error(`Accessors are not supported in canonical JSON v1 at ${path}.${key}`);
      }

      return `${JSON.stringify(key)}:${canonicalizeValue(descriptor.value, `${path}.${key}`, ancestors)}`;
   });

   ancestors.delete(value);
   return `{${canonicalProperties.join(',')}}`;
}

function validatePlainObject(value, path) {
   const prototype = Object.getPrototypeOf(value);

   if (prototype !== Object.prototype && prototype !== null) {
      throw new CanonicalJsonV1Error(`Only plain objects are supported in canonical JSON v1 at ${path}`);
   }
}

function rejectCycle(value, path, ancestors) {
   if (ancestors.has(value)) {
      throw new CanonicalJsonV1Error(`Cycles are not supported in canonical JSON v1 at ${path}`);
   }

   ancestors.add(value);
}

module.exports = {
   CanonicalJsonV1Error,
   OFFLINE_PROTOCOL_CANONICALIZATION_VERSION_V1,
   OFFLINE_PROTOCOL_ENVELOPE_VERSION_V1,
   OFFLINE_PROTOCOL_DOMAIN_LABELS_V1,
   canonicalizeJsonV1,
   buildCanonicalEnvelopeV1,
   canonicalizeEnvelopeV1,
   canonicalizeActionDigestEnvelopeV1,
   canonicalizeProvisionalEventDigestEnvelopeV1,
   canonicalizeServerAcceptanceFingerprintEnvelopeV1,
   canonicalizeLocalDeclarationFingerprintEnvelopeV1
};
